<script lang="ts" setup>
/* eslint-disable */
import {
  ref, watch, computed, onMounted, Ref,
} from 'vue';
import { TransitionRoot, TransitionChild } from '@headlessui/vue';
import {
  getInvoiceSubmissionStatusAsync,
  submitInvoiceToChorusAsync,
} from '../../api/chorus/chorusInvoiceSubmissionApi';
import InvoiceSubmissionRequest from '../../models/Chorus/ChorusInvoiceSubmissionRequest';
import DrawerData from './Drawer.json';
import LoadingSpin from '../Shared/LoadingSpin.vue';
import Select from '../Shared/Select.vue';
import DrawerDetail from './DrawerDetail.vue';
import { getSlug } from '../../api/slugApi';
import useChorusStore from '../../store/Chorus';
import { useStore } from '../../store';
import { types as configTypes } from '../../store/Configuration';
import { useI18n } from '../../i18n';
import SubmissionState from './SubmissionState.vue';

const store = useStore();
const chorusStore = useChorusStore();
const { t } = useI18n();

chorusStore.loadConfiguration();

const loading = ref(true);
const error = ref(null) as Ref<boolean | null>;
const errorMsg = ref('');
const selectedTaxType = ref() as Ref<string | undefined>;
const selectedPaymentMethod = ref() as Ref<string | undefined>;
const selectedInvoicingContext = ref() as Ref<string | undefined>;
const selectedExemptionReason = ref() as Ref<string | undefined>;
const selectedCommitmentForm = ref() as Ref<string | undefined>;
const commitmentNumberFreeValue = ref() as Ref<string | undefined>;
const codeStructureValideur = ref() as Ref<string | undefined>;
const codeServiceExecutant = ref() as Ref<string | undefined>;
const drawerData = ref(DrawerData);
const loadingChorus = ref(false);
const slug = getSlug();

onMounted(async () => {
  loadingChorus.value = true;
  loadingChorus.value = false;
});

const commitmentNumberForm = computed(() => {
  let disableWithoutContractNumberOption = false;
  let disableWithContractNumberOption = false;

  for (let i = 0; i < chorusStore.selected.length; i += 1) {
    if (!chorusStore.selected[i].commitmentNumber) {
      disableWithoutContractNumberOption = true;
      disableWithContractNumberOption = true;
    } else if (!chorusStore.selected[i].contractNumber) disableWithContractNumberOption = true;
  }

  const values = [
    { name: "Sans numéro d'engagement", value: undefined },
    {
      name: 'Numéro de commande seul',
      value: 'withoutContractNumber',
      disabled: disableWithoutContractNumberOption,
    },
    {
      name: '<Numéro de commande>_<Numéro de marché>',
      value: 'withContractNumber',
      disabled: disableWithContractNumberOption,
    },
  ];

  if (chorusStore.selected.length === 1) {
    values.push({
      name: 'Saisie libre',
      value: 'custom',
      disabled: false,
    });
  }

  return values;
});

const hasUnsendedInvoices = computed(() => {
  for (let i = 0; i < chorusStore.selected.length; i += 1) {
    if (
      chorusStore.selected[i].state?.status === 'REJECTED'
      || chorusStore.selected[i].state?.status === 'ERROR'
      || chorusStore.selected[i].state?.status === undefined
    ) return true;
  }
  return false;
});

const hasValidInvoice = computed(() => {
  for (let i = 0; i < chorusStore.selected.length; i += 1) {
    if (
      !chorusStore.selected[i].invalidData.length
      && chorusStore.selected[i].state?.status !== 'ACCEPTED'
      && chorusStore.selected[i].state?.status !== 'ERROR'
    ) return true;
  }
  return false;
});

watch(
  () => store.state.config.rightDrawer,
  () => {
    if (!store.state.config.rightDrawer) {
      error.value = null;
      selectedTaxType.value = '';
      selectedPaymentMethod.value = '';
      selectedInvoicingContext.value = '';
      selectedExemptionReason.value = '';
      codeStructureValideur.value = '';
      codeServiceExecutant.value = '';
    }
  },
);

const send = async () => {
  error.value = null;

  if (
    !selectedInvoicingContext.value
    || !selectedPaymentMethod.value
    || !selectedTaxType.value
    || (selectedTaxType.value === 'EXONERATION'
      && !selectedExemptionReason.value)
    || ((selectedInvoicingContext.value === 'A9_FACTURE_SOUSTRAITANT'
      || selectedInvoicingContext.value === 'A12_FACTURE_COTRAITANT')
      && !codeStructureValideur.value)
  ) {
    error.value = true;
    errorMsg.value = 'Informations manquantes :';
    if (!selectedTaxType.value) errorMsg.value += '\n- Type de TVA';
    if (!selectedInvoicingContext.value) errorMsg.value += '\n- Cadre de facturation';
    if (!selectedPaymentMethod.value) errorMsg.value += '\n- Mode de réglement';
    if (
      selectedTaxType.value === 'EXONERATION'
      && !selectedExemptionReason.value
    ) errorMsg.value += "\n- Motif d'éxonération";
    if (
      (selectedInvoicingContext.value === 'A9_FACTURE_SOUSTRAITANT'
        || selectedInvoicingContext.value === 'A12_FACTURE_COTRAITANT')
      && !codeStructureValideur.value
    ) errorMsg.value += '\n- Code de la structure du valideur manquant';
    return;
  }

  loading.value = true;
  let hasError = false;
  let rejectionDetails = '';

  for (let i = 0; i < chorusStore.selected.length; i += 1) {
    if (
      !chorusStore.selected[i].invalidData.length
      && chorusStore.selected[i].state?.status !== 'ACCEPTED'
      && chorusStore.configuration?.id
    ) {
      const model: InvoiceSubmissionRequest = {
        invoiceId: chorusStore.selected[i].id,
        invoicingContext: selectedInvoicingContext.value,
        paymentMethod: selectedPaymentMethod.value,
        taxType: selectedTaxType.value,
        exemptionReason: selectedExemptionReason.value,
        validatorStructureCode: codeStructureValideur.value,
        configurationId: chorusStore.configuration?.id,
        serviceId: codeServiceExecutant.value,
      };

      if (selectedCommitmentForm.value === 'withContractNumber') {
        model.commitmentNumber = `${chorusStore.selected[i].commitmentNumber
        }_${
          chorusStore.selected[i].contractNumber}`;
      } else if (selectedCommitmentForm.value === 'withoutContractNumber') model.commitmentNumber = chorusStore.selected[i].commitmentNumber;
      else if (selectedCommitmentForm.value === 'custom') model.commitmentNumber = commitmentNumberFreeValue.value;

      try {
        await submitInvoiceToChorusAsync(model);
        const response = await getInvoiceSubmissionStatusAsync(
          1,
          model.invoiceId,
        );
        if (response.status === 'REJECTED') {
          hasError = true;
          if (response.message.libelle) {
            rejectionDetails
              += `Pour la facture n°${
                chorusStore.selected[i].reference
              }: \n -${
                response.message.libelle.split('-')[1]}`;
          } else {
            rejectionDetails
              += `Pour la facture n°${
                chorusStore.selected[i].reference
              }: \n -${
                response.message}`;
          }
        }
      } catch (e) {
        errorMsg.value = "Une erreur s'est produite,\n veuillez réessayer ultérieurement";
        hasError = true;
      }
    }
  }
  loading.value = false;
  if (hasError) {
    error.value = true;
    errorMsg.value = rejectionDetails;
  } else {
    error.value = false;
    setTimeout(() => {
      store.commit(configTypes.TOOGLE_DRAWER_RIGHT, false);
      chorusStore.setSelected([]);
      loading.value = false;
    }, 1200);
  }
  chorusStore.loadStatus();
};

loading.value = false;
</script>

<template>
  <div class="h-full flex flex-col bg-white">
    <!-- Header du drawer -->
    <div class="px-6 py-3 border-b border-gray-200 bg-white">
      <div class="flex justify-between items-start">
        <div class="flex-1 min-w-0">
          <div v-if="chorusStore.selected.length === 1" class="space-y-1">
            <div class="flex items-center space-x-2">
              <h2 class="text-base font-semibold text-gray-900 truncate">
                Facture n°{{ chorusStore.selected[0].reference }}
              </h2>
              <div class="flex-shrink-0">
                <SubmissionState :data="chorusStore.selected[0]" />
              </div>
            </div>
            <p class="text-xs text-gray-500">
              {{ new Date(chorusStore.selected[0].date).toLocaleDateString('fr-FR') }}
            </p>
            <p 
              v-if="chorusStore.selected[0].state?.statusComment" 
              class="text-xs italic leading-relaxed mt-1"
              :class="{
                'text-red-600': chorusStore.selected[0].state?.status === 'REJETEE' || chorusStore.selected[0].state?.status === 'REFUSEE_1' || chorusStore.selected[0].state?.status === 'ERROR',
                'text-orange-600': chorusStore.selected[0].state?.status === 'PENDING',
                'text-gray-600': !['REJETEE', 'REFUSEE_1', 'ERROR', 'PENDING'].includes(chorusStore.selected[0].state?.status || '')
              }"
            >
              {{ chorusStore.selected[0].state.statusComment }}
            </p>
          </div>
          <div v-else class="space-y-1">
            <h2 class="text-base font-semibold text-gray-900">
              {{ chorusStore.selected.length }} factures
            </h2>
            <p class="text-xs text-gray-500">
              Envoi groupé vers Chorus Pro
            </p>
          </div>
        </div>
        
        <button
          @click="
            store.commit(configTypes.TOOGLE_DRAWER_RIGHT, false);
            chorusStore.setSelected([]);
          "
          class="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <fa
            :icon="['fal', 'xmark']"
            class="w-4 h-4 text-gray-500"
          />
        </button>
      </div>
    </div>

    <!-- Actions rapides -->
    <div v-if="chorusStore.selected.length === 1" class="px-6 py-2 bg-gray-50 border-b border-gray-200">
      <a
        :href="
          'https://' +
            slug +
            '.teo-online.net/compta/facture_edit.php?numfacture=' +
            chorusStore.selected[0].reference
        "
        target="_blank"
        class="inline-flex items-center px-2 py-1.5 border border-blue-333 text-blue-333 bg-white rounded text-xs font-medium hover:bg-blue-50 transition-colors"
        title="Modifier la facture dans TéO"
      >
        <img
          src="./../../assets/logo_fond_bleu.svg"
          alt="TéO"
          class="w-3 h-3 mr-1.5"
        >
        Modifier
      </a>
    </div>

    <!-- Erreurs de validation -->
    <div v-if="chorusStore.selected.some(invoice => invoice.invalidData.length)" class="bg-red-50 border-b border-red-200">
      <div class="px-6 py-3 space-y-3">
        <div class="flex items-start space-x-3">
          <fa
            :icon="['fas', 'triangle-exclamation']"
            class="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0"
          />
          <div class="flex-1">
            <h3 class="text-xs font-medium text-red-800">
              Factures non conformes
            </h3>
            <p class="text-xs text-red-700 mt-1">
              Les factures suivantes ne peuvent pas être envoyées :
            </p>
          </div>
        </div>
        
        <div class="space-y-2">
          <div
            v-for="invoice in chorusStore.selected.filter(inv => inv.invalidData.length)"
            :key="invoice.reference"
            class="bg-white rounded p-2 border border-red-200"
          >
            <div class="flex items-center justify-between mb-1">
              <span class="font-medium text-red-800 text-xs">
                {{ invoice.reference }}
              </span>
              <a
                :href="
                  'http://' +
                    slug +
                    '.teo-online.net/compta/facture_edit.php?numfacture=' +
                    invoice.reference
                "
                target="_blank"
                class="text-xs text-red-600 hover:text-red-800 underline"
                title="Modifier la facture dans TéO"
              >
                Corriger
              </a>
            </div>
            <ul class="list-disc ml-3 text-xs text-red-700 space-y-0.5">
              <li
                v-for="error in invoice.invalidData"
                :key="error"
              >
                {{ t("chorus_pro_error_" + error) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenu scrollable -->
    <div class="flex-1 overflow-y-auto">
      <div
        v-if="
          hasUnsendedInvoices &&
            (chorusStore.selected.length > 1 ||
              (chorusStore.selected.length === 1 &&
                chorusStore.selected[0].invalidData.length === 0))
        "
        class="p-4 space-y-4"
      >
        <!-- Message d'information ou d'erreur -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
          <div class="flex items-start space-x-2">
            <fa
              :icon="['fas', 'circle-xmark']"
              class="w-4 h-4 text-red-600 mt-0.5"
            />
            <div class="text-xs text-red-800">
              <p class="font-medium mb-1">Erreur lors de l'envoi</p>
              <pre class="whitespace-pre-wrap text-xs">{{ errorMsg }}</pre>
            </div>
          </div>
        </div>
        
        <div v-else class="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div class="flex items-start space-x-2">
            <fa
              :icon="['fas', 'info-circle']"
              class="w-4 h-4 text-blue-600 mt-0.5"
            />
            <div class="text-xs text-blue-800">
              <p class="font-medium mb-1">Informations requises</p>
              <p>Complétez les données ci-dessous avant l'envoi.</p>
            </div>
          </div>
        </div>

        <!-- Formulaire -->
        <div class="space-y-4">
          <!-- Type de TVA -->
          <div class="space-y-1.5">
            <label class="flex items-center text-xs font-medium text-gray-700">
              Type de TVA
              <span class="text-red-500 ml-1">*</span>
            </label>
            <div class="drawer-field">
              <Select
                v-model="selectedTaxType"
                :options="drawerData['taxType']"
                :selectClass="error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''"
                class="w-full"
              />
            </div>
          </div>

          <!-- Motif d'exonération (conditionnel) -->
          <TransitionRoot :show="selectedTaxType === 'EXONERATION'">
            <TransitionChild
              enter="transition-opacity ease-linear duration-150"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="transition-opacity ease-linear duration-150"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="space-y-1.5">
                <label class="flex items-center text-xs font-medium text-gray-700">
                  Motif d'exonération
                  <span class="text-red-500 ml-1">*</span>
                </label>
                <div class="drawer-field">
                  <Select
                    v-model="selectedExemptionReason"
                    :options="drawerData['exemptionReason']"
                    :selectClass="error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''"
                    class="w-full"
                  />
                </div>
              </div>
            </TransitionChild>
          </TransitionRoot>

          <!-- Cadre de facturation -->
          <div class="space-y-1.5">
            <label class="flex items-center text-xs font-medium text-gray-700">
              Cadre de facturation
              <span class="text-red-500 ml-1">*</span>
            </label>
            <div class="drawer-field">
              <Select
                v-model="selectedInvoicingContext"
                :options="drawerData['invoicingContext']"
                :selectClass="error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''"
                class="w-full"
              />
            </div>
          </div>

          <!-- Code structure valideur (conditionnel) -->
          <TransitionRoot
            :show="
              selectedInvoicingContext === 'A9_FACTURE_SOUSTRAITANT' ||
                selectedInvoicingContext === 'A12_FACTURE_COTRAITANT'
            "
          >
            <TransitionChild
              enter="transition-opacity ease-linear duration-150"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="transition-opacity ease-linear duration-150"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="space-y-1.5">
                <label class="flex items-center text-xs font-medium text-gray-700">
                  Code structure valideur
                  <span class="text-red-500 ml-1">*</span>
                </label>
                <input
                  v-model="codeStructureValideur"
                  type="text"
                  placeholder="Code de la structure valideur"
                  class="w-full h-8 px-2 py-1 text-xs bg-white border rounded focus:ring focus:ring-opacity-50"
                  :class="
                    error
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-333 focus:border-blue-333 focus:ring-blue-333/20'
                  "
                />
              </div>
            </TransitionChild>
          </TransitionRoot>

          <!-- Mode de règlement -->
          <div class="space-y-1.5">
            <label class="flex items-center text-xs font-medium text-gray-700">
              Mode de règlement
              <span class="text-red-500 ml-1">*</span>
            </label>
            <div class="drawer-field">
              <Select
                v-model="selectedPaymentMethod"
                :options="drawerData['paymentMethod']"
                :selectClass="error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''"
                class="w-full"
              />
            </div>
          </div>

          <!-- Numéro d'engagement -->
          <div class="space-y-1.5">
            <label class="block text-xs font-medium text-gray-700">
              Numéro d'engagement
            </label>
            <div class="drawer-field">
              <Select
                v-model="selectedCommitmentForm"
                :options="commitmentNumberForm"
                :selectClass="error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''"
                class="w-full"
              />
            </div>
          </div>

          <!-- Saisie libre numéro d'engagement (conditionnel) -->
          <TransitionRoot :show="selectedCommitmentForm === 'custom'">
            <TransitionChild
              enter="transition-opacity ease-linear duration-150"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="transition-opacity ease-linear duration-150"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="space-y-1.5">
                <label class="block text-xs font-medium text-gray-700">
                  Numéro d'engagement personnalisé
                </label>
                <input
                  v-model="commitmentNumberFreeValue"
                  type="text"
                  placeholder="Numéro d'engagement"
                  class="w-full h-8 px-2 py-1 text-xs bg-white border rounded focus:ring focus:ring-opacity-50"
                  :class="
                    error
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-333 focus:border-blue-333 focus:ring-blue-333/20'
                  "
                />
              </div>
            </TransitionChild>
          </TransitionRoot>

          <!-- Code service exécutant -->
          <div class="space-y-1.5">
            <label class="block text-xs font-medium text-gray-700">
              Code service exécutant
            </label>
            <input
              v-model="codeServiceExecutant"
              type="text"
              placeholder="Code du service exécutant"
              class="w-full h-8 px-2 py-1 text-xs bg-white border rounded focus:ring focus:ring-opacity-50"
              :class="
                error
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-333 focus:border-blue-333 focus:ring-blue-333/20'
              "
            />
          </div>
        </div>
      </div>

      <!-- Détails supplémentaires pour facture unique -->
      <DrawerDetail
        v-if="chorusStore.selected.length === 1"
        class="border-t border-gray-200 bg-gray-50"
      />
    </div>

    <!-- Footer avec bouton d'envoi -->
    <div class="border-t border-gray-200 p-4 bg-white">
      <button
        :disabled="
          loading ||
            !hasUnsendedInvoices ||
            !hasValidInvoice ||
            !chorusStore.configuration
        "
        @click="send"
        class="w-full inline-flex items-center justify-center px-3 py-2 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :class="
          !chorusStore.configuration || !hasValidInvoice
            ? 'bg-gray-200 text-gray-500 border border-gray-300'
            : 'bg-blue-333 text-white hover:bg-blue-400 border border-blue-333'
        "
      >
        <LoadingSpin v-if="loading" class="w-3 h-3 mr-1.5" />
        <fa
          v-else-if="error === false && !loading"
          :icon="['fas', 'circle-check']"
          class="w-3 h-3 mr-1.5 text-green-500"
        />
        <fa
          v-else-if="error === true && !loading"
          :icon="['fas', 'circle-xmark']"
          class="w-3 h-3 mr-1.5 text-red-500"
        />
        
        <span v-if="!chorusStore.configuration">
          Aucun compte Chorus Pro
        </span>
        <span v-else-if="!hasUnsendedInvoices">
          {{ chorusStore.selected.length > 1 ? "Factures envoyées" : "Facture envoyée" }}
        </span>
        <span v-else-if="!hasValidInvoice">
          {{ t("send_to_chorus_pro") }}
        </span>
        <span v-else>
          {{ t("send_to_chorus_pro") }}
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.drawer-field :deep(select) {
  @apply w-full h-10 px-3 py-2 text-sm bg-white border border-gray-333 rounded-lg focus:border-blue-333 focus:ring focus:ring-blue-333/20 mt-0;
}

.drawer-field :deep(.fa-caret-down) {
  @apply text-gray-400;
}
</style>
